import { useCallback, useMemo } from "react";

import { styled } from "@linaria/react";
import { BuildingsIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import Button, { ButtonVariant } from "@gx/dls/buttons/Button";
import Chip, { ChipSize, ChipVariant } from "@gx/dls/chips/Chip";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import Spacing from "@gx/dls/containers/Spacing";
import Text, { TextVariant, TextWeight } from "@gx/dls/text/Text";
import { GalaxyTheme } from "@gx/dls/theme/constants";
import { useGalaxyTheme, withTheme } from "@gx/dls/theme/GalaxyTheme";
import { PropsWithTheme } from "@gx/dls/theme/types";
import Tooltip from "@gx/dls/tooltip/Tooltip";
import Widget, { WidgetVariant } from "@gx/dls/widget/Widget";
import { LoggingId, posthogEvent } from "@gx/logging";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import {
  SOURCE_TYPE_TO_DESCRIPTION_MAP,
  SOURCE_TYPE_TO_ICON_MAP,
  SOURCE_TYPE_TO_LABEL_MAP,
  SOURCE_TYPE_TO_SOURCE_TYPE_STATUS_CHIP_MAP,
  SourceTypeStatusChip,
} from "@/pages/sources/constants";
import { useSourceTypeEnabled } from "@/pages/sources/hooks/useSourceTypeEnabled";

interface CreateSourceSelectorCardProps {
  sourceType: SourceType;
  onSelect: (sourceType: SourceType) => void;
}

const CardWrapper = styled.div<{ $hasStatusChip: boolean }>`
  cursor: pointer;
  height: 100%;
  opacity: ${({ $hasStatusChip }) => ($hasStatusChip ? 0.5 : 1)};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const IconWrapper = withTheme(styled.div<PropsWithTheme & { $isLightMode: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 5px;
  width: 28px;
  height: 28px;
  background-color: ${({ theme, $isLightMode }) =>
    $isLightMode ? theme.color.opacity.bw100 : theme.color.opacity.bw100Alt};
`);

const CreateSourceSelectorCardHeader = ({
  sourceType,
}: Pick<CreateSourceSelectorCardProps, "sourceType">) => {
  const { galaxyTheme } = useGalaxyTheme();
  const isLightMode = useMemo(() => galaxyTheme === GalaxyTheme.LIGHT, [galaxyTheme]);

  const renderChip = () => {
    return match(SOURCE_TYPE_TO_SOURCE_TYPE_STATUS_CHIP_MAP[sourceType])
      .with(SourceTypeStatusChip.NONE, () => null)
      .with(SourceTypeStatusChip.ENTERPRISE, () => (
        <Tooltip body="Enterprise Plan required">
          <Chip icon={BuildingsIcon} variant={ChipVariant.BLUE} isPill />
        </Tooltip>
      ))
      .with(SourceTypeStatusChip.COMING_SOON, () => (
        <Chip label="Coming soon" variant={ChipVariant.YELLOW} size={ChipSize.SMALL} isPill />
      ))
      .exhaustive();
  };

  return (
    <FlexWrapper
      justifyContent={JustifyContent.SPACE_BETWEEN}
      alignItems={AlignItems.CENTER}
      fillWidth
    >
      <FlexWrapper gap={8} alignItems={AlignItems.CENTER}>
        <IconWrapper $isLightMode={isLightMode}>{SOURCE_TYPE_TO_ICON_MAP[sourceType]}</IconWrapper>
        <Text weight={TextWeight.MEDIUM}>{SOURCE_TYPE_TO_LABEL_MAP[sourceType]}</Text>
      </FlexWrapper>
      {renderChip()}
    </FlexWrapper>
  );
};

const CreateSourceSelectorCardBody = ({
  sourceType,
}: Pick<CreateSourceSelectorCardProps, "sourceType">) => {
  return (
    <FlexWrapper gap={3} alignItems={AlignItems.CENTER}>
      <Text variant={TextVariant.TERTIARY}>{SOURCE_TYPE_TO_DESCRIPTION_MAP[sourceType]}</Text>
    </FlexWrapper>
  );
};

const CreateSourceSelectorCard = ({ sourceType, onSelect }: CreateSourceSelectorCardProps) => {
  const isEnabled = useSourceTypeEnabled(sourceType);
  const label = SOURCE_TYPE_TO_LABEL_MAP[sourceType];

  const handleClick = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      onSelect(sourceType);
      posthogEvent(LoggingId.SOURCES_CREATE_SOURCE_SELECTOR_SOURCE_TYPE_SELECTED, {
        source_type: sourceType,
      });
    },
    [sourceType, onSelect],
  );

  const handleContactUs = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      const subject =
        sourceType === SourceType.UNSPECIFIED
          ? encodeURIComponent("Interested in a new source integration")
          : encodeURIComponent(`Looking to use the ${label} source`);
      window.location.href = `mailto:hello@getgalaxy.io?subject=${subject}`;
    },
    [label, sourceType],
  );

  const showContactUs = sourceType === SourceType.UNSPECIFIED || !isEnabled;
  const handleCardClick = showContactUs ? handleContactUs : handleClick;

  const widgetVariant = (() => {
    if (sourceType === SourceType.UNSPECIFIED) {
      return WidgetVariant.PRIMARY;
    }
    if (isEnabled) {
      return WidgetVariant.TERTIARY;
    }
    return WidgetVariant.BASE;
  })();

  return (
    <CardWrapper
      onClick={handleCardClick}
      $hasStatusChip={
        SOURCE_TYPE_TO_SOURCE_TYPE_STATUS_CHIP_MAP[sourceType] !== SourceTypeStatusChip.NONE
      }
    >
      <Widget
        fillWidth
        fillHeight
        minHeight={150}
        variant={widgetVariant}
        onClick={handleCardClick}
      >
        <FlexWrapper
          direction={FlexDirection.COLUMN}
          justifyContent={JustifyContent.SPACE_BETWEEN}
          gap={8}
          fillHeight
        >
          <FlexWrapper gap={8} direction={FlexDirection.COLUMN}>
            <CreateSourceSelectorCardHeader sourceType={sourceType} />
            <CreateSourceSelectorCardBody sourceType={sourceType} />
          </FlexWrapper>
          <Spacing top={4} fillWidth>
            {showContactUs ? (
              <Button
                label={sourceType === SourceType.UNSPECIFIED ? "Get support" : "Contact us"}
                variant={ButtonVariant.SECONDARY}
                onClick={handleContactUs}
                fillWidth
              />
            ) : (
              <Button label="Create source" onClick={handleClick} fillWidth />
            )}
          </Spacing>
        </FlexWrapper>
      </Widget>
    </CardWrapper>
  );
};

export default CreateSourceSelectorCard;
